import { useEffect, useRef } from 'react'

interface MarkReadOnArrivalParams {
  conversationId: string
  /** Id of the newest message someone else sent (see `latestReceivedMessageId`). */
  latestReceivedId: string | undefined
  /** Older messages are not loaded yet: one of them may be the unread one. */
  hasOlderMessages: boolean
  /** The conversation is in front of the user (see `useIsScreenLive`). */
  isLive: boolean
  /** The messages on screen are current, not an old list read from the cache. */
  ready: boolean
  markRead: (conversationId: string) => void
}

/**
 * The conversation is read as soon as its current messages are in front of
 * the user: on opening, whatever the way in (list, notification, profile,
 * search…), and each time a message arrives.
 *
 * Not while the tab is hidden or the app in the background: it is then marked
 * when the user comes back. Not from an old list read from the cache
 * (`ready`).
 */
export const useMarkReadOnArrival = ({
  conversationId,
  latestReceivedId,
  hasOlderMessages,
  isLive,
  ready,
  markRead,
}: MarkReadOnArrivalParams): void => {
  const seenReceivedRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!isLive || !ready) return
    const seen = `${conversationId}:${latestReceivedId ?? ''}`
    if (seenReceivedRef.current === seen) return
    seenReceivedRef.current = seen
    // Nothing received from someone else, in a conversation loaded to its
    // first message: nothing to read.
    if (!latestReceivedId && !hasOlderMessages) return
    markRead(conversationId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestReceivedId, isLive, conversationId, ready])
}
